import { enumerateTargetCandidates, targetCandidateKey, validateAndCanonicalizeTargetSelection } from './enumerate.js';
function tokenOf(choiceId, index) {
    // The token exposes only the deterministic choice-local ordinal, never an auth id/defId.
    return `${choiceId}.t${index.toString(36)}`;
}
function publicLabel(candidate, request) {
    if (candidate.kind !== 'mode' || request.spec.kind !== 'mode')
        return undefined;
    return request.spec.options.find((option) => option.id === candidate.id)?.label ?? candidate.id;
}
export function materializeChoice(choiceId, request, context) {
    if (request.spec.onInsufficient === undefined) {
        throw new Error('ctx.choose: TargetSpec.onInsufficient должен быть задан явно для runtime choice');
    }
    const previous = request.previous ?? [];
    const domain = enumerateTargetCandidates(request.spec, context, previous);
    if (domain.length === 0 && request.spec.cardinality.min === 0) {
        return { status: 'skip', selection: [] };
    }
    if (domain.length < request.spec.cardinality.min) {
        const policy = request.spec.onInsufficient;
        if (policy === 'skip')
            return { status: 'skip', selection: [] };
        if (policy === 'fizzle')
            return { status: 'fizzle' };
        return {
            status: 'match_error',
            reason: `choice "${choiceId}" имеет ${domain.length} кандидатов при min=${request.spec.cardinality.min}`,
        };
    }
    const candidates = domain.map((candidate, index) => {
        const choiceToken = tokenOf(choiceId, index);
        const label = publicLabel(candidate, request);
        return {
            choiceToken,
            candidate: structuredClone(candidate),
            ...(label !== undefined ? { label } : {}),
        };
    });
    const targetByToken = new Map(candidates.map(({ choiceToken, candidate }) => [choiceToken, candidate]));
    return {
        status: 'pending',
        descriptor: {
            choiceId,
            playerId: request.who,
            cardinality: structuredClone(request.spec.cardinality),
            ordered: request.spec.ordered === true,
            candidates,
            targetByToken,
            domain,
        },
    };
}
export function pendingChoiceOf(descriptor, prompt) {
    return {
        choiceId: descriptor.choiceId,
        playerId: descriptor.playerId,
        cardinality: structuredClone(descriptor.cardinality),
        ordered: descriptor.ordered,
        candidates: descriptor.candidates.map((candidate) => structuredClone(candidate)),
        ...(prompt !== undefined ? { prompt } : {}),
    };
}
export function resolveChoiceTokens(descriptor, request, tokens, liveContext) {
    if (!Array.isArray(tokens) || tokens.some((token) => typeof token !== 'string' || token === '')) {
        throw new Error('Resolve.tokens должен быть массивом непустых choiceToken');
    }
    if (new Set(tokens).size !== tokens.length)
        throw new Error('Resolve: повтор choiceToken запрещён');
    const selection = tokens.map((token) => {
        const target = descriptor.targetByToken.get(token);
        if (target === undefined)
            throw new Error(`Resolve: choiceToken "${token}" отсутствует в исходном domain`);
        return target;
    });
    const canonical = validateAndCanonicalizeTargetSelection(request.spec, descriptor.domain, selection);
    const liveDomain = enumerateTargetCandidates(request.spec, liveContext, request.previous ?? []);
    const liveKeys = new Set(liveDomain.map(targetCandidateKey));
    for (const candidate of canonical) {
        if (!liveKeys.has(targetCandidateKey(candidate))) {
            throw new Error(`Resolve: цель "${targetCandidateKey(candidate)}" больше не проходит live predicate`);
        }
    }
    return canonical;
}
export function tokensForSelection(descriptor, selection) {
    const tokenByKey = new Map(descriptor.candidates.map(({ choiceToken, candidate }) => [targetCandidateKey(candidate), choiceToken]));
    return selection.map((candidate) => {
        const token = tokenByKey.get(targetCandidateKey(candidate));
        if (token === undefined)
            throw new Error(`цель "${targetCandidateKey(candidate)}" отсутствует в choice descriptor`);
        return token;
    });
}
//# sourceMappingURL=choice.js.map